/**
 * DemandTagPicker — apply per-FA tags to a demand, with inline create.
 *
 * Mirrors SkillTypeaheadChipList's UX: applied tags show as removable chips,
 * and an input lets you either pick an existing tag from the FA's catalog or
 * type a new label and **"Create 'X'"** — which creates it in the FA catalog
 * and applies it in one step (like the freehand add-a-person flow). Read-only
 * (non-editors) just see the chips.
 */

import { useState } from 'react'
import { Spinner, makeStyles, tokens } from '@fluentui/react-components'
import { Dismiss12Regular } from '@fluentui/react-icons'
import { useCreateFaTag, useFaTags } from '../hooks/useFocusAreas'
import { useSetDemandTags } from '../hooks/useDemands'
import { apiErrorMessage } from '../lib/apiError'
import { useToast } from './ToastProvider'
import type { Demand } from '../types/demand'
import { bayerColors, surfaces } from '../theme'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '6px' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '9999px',
    background: bayerColors.navyLight,
    color: bayerColors.navy,
    fontSize: '12px',
    fontWeight: 600,
  },
  chipX: {
    cursor: 'pointer',
    display: 'inline-flex',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: bayerColors.navy,
    ':hover': { color: '#b10e1c' },
  },
  empty: { fontSize: '13px', color: tokens.colorNeutralForeground3 },
  inputWrapper: { position: 'relative', display: 'inline-flex', flexDirection: 'column' },
  input: {
    fontSize: '12px',
    padding: '5px 10px',
    borderRadius: '9999px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    outline: 'none',
    background: '#FFFFFF',
    minWidth: '180px',
    ':focus': {
      borderTopColor: bayerColors.navyMid,
      borderRightColor: bayerColors.navyMid,
      borderBottomColor: bayerColors.navyMid,
      borderLeftColor: bayerColors.navyMid,
      boxShadow: '0 0 0 3px rgba(43,107,166,0.12)',
    },
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
    background: '#FFFFFF',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: '6px',
    boxShadow: tokens.shadow8,
    zIndex: 1000,
    padding: '4px 0',
  },
  option: {
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': { background: surfaces.proposalCardBg },
  },
  optionActive: {
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: bayerColors.navyLight,
  },
  createOption: {
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontStyle: 'italic',
    color: bayerColors.navyMid,
    ':hover': { background: surfaces.proposalCardBg },
  },
  addButton: {
    fontSize: '12px',
    color: bayerColors.navyMid,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '3px 6px',
    borderRadius: '4px',
    ':hover': { background: bayerColors.navyLight },
  },
})

export function DemandTagPicker({ demand, canEdit }: { demand: Demand; canEdit: boolean }) {
  const styles = useStyles()
  const toast = useToast()
  const { data: catalog, isLoading } = useFaTags(canEdit ? demand.focus_area_code : undefined)
  const createTag = useCreateFaTag()
  const setTags = useSetDemandTags()

  const applied = demand.tags ?? []
  const appliedIds = applied.map((t) => t.id)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)

  const busy = setTags.isPending || createTag.isPending
  const draftTrimmed = draft.trim()
  const draftLower = draftTrimmed.toLowerCase()
  const cat = catalog ?? []
  const suggestions = cat.filter(
    (t) => !appliedIds.includes(t.id) && t.label.toLowerCase().includes(draftLower),
  )
  const exactMatch = cat.some((t) => t.label.toLowerCase() === draftLower)
  const showCreate = draftTrimmed.length > 0 && !exactMatch
  const totalOptions = (showCreate ? 1 : 0) + suggestions.length

  function onErr(err: unknown, fallback: string) {
    toast.showToast({ title: apiErrorMessage(err, fallback), intent: 'error' })
  }

  function applyTagIds(ids: number[]) {
    setTags.mutate(
      { demandId: demand.id, tagIds: ids },
      { onError: (e) => onErr(e, 'Could not update tags') },
    )
  }

  function reset() {
    setDraft('')
    setActiveIdx(-1)
    setShowDropdown(false)
  }

  function applyExisting(tagId: number) {
    if (!appliedIds.includes(tagId)) applyTagIds([...appliedIds, tagId])
    reset()
  }

  function createAndApply(label: string) {
    createTag.mutate(
      { code: demand.focus_area_code, body: { label } },
      {
        onSuccess: (tag) => applyTagIds([...appliedIds, tag.id]),
        onError: (e) => onErr(e, 'Could not create tag'),
      },
    )
    reset()
  }

  function removeTag(tagId: number) {
    // Undo rather than a confirmation (#1245). Removing a tag is one click and
    // fully reversible — the endpoint takes the whole list, so putting it back
    // is the same call with the old array. A prompt would tax every correct
    // removal to catch the rare wrong one; the offer costs nothing until used.
    const before = appliedIds
    const removed = applied.find((t) => t.id === tagId)
    setTags.mutate(
      { demandId: demand.id, tagIds: before.filter((id) => id !== tagId) },
      {
        onSuccess: () =>
          toast.showToast({
            title: `Removed ${removed?.label ?? 'tag'}`,
            action: { label: 'Undo', onClick: () => applyTagIds(before) },
          }),
        onError: (e) => onErr(e, 'Could not update tags'),
      },
    )
  }

  function commitActive() {
    if (activeIdx >= 0) {
      if (showCreate && activeIdx === 0) createAndApply(draftTrimmed)
      else {
        const sIdx = showCreate ? activeIdx - 1 : activeIdx
        if (suggestions[sIdx]) applyExisting(suggestions[sIdx].id)
      }
    } else if (draftTrimmed) {
      const existing = cat.find((t) => t.label.toLowerCase() === draftLower)
      if (existing) applyExisting(existing.id)
      else createAndApply(draftTrimmed)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((p) => Math.min(p + 1, totalOptions - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((p) => Math.max(p - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      commitActive()
    } else if (e.key === 'Escape') {
      setAdding(false)
      reset()
    }
  }

  // Read-only: just the chips.
  if (!canEdit) {
    if (applied.length === 0) return <span className={styles.empty}>No tags.</span>
    return (
      <div className={styles.chipRow}>
        {applied.map((t) => (
          <span key={t.id} className={styles.chip} data-testid={`demand-tag-${t.id}`}>
            {t.label}
          </span>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.chipRow}>
        {applied.map((t) => (
          <span key={t.id} className={styles.chip} data-testid={`demand-tag-${t.id}`}>
            {t.label}
            <button
              type="button"
              className={styles.chipX}
              onClick={() => removeTag(t.id)}
              aria-label={`Remove ${t.label}`}
              disabled={busy}
            >
              <Dismiss12Regular />
            </button>
          </span>
        ))}
        {busy && <Spinner size="tiny" />}
      </div>

      {adding ? (
        <div className={styles.inputWrapper}>
          <input
            type="text"
            autoFocus
            className={styles.input}
            value={draft}
            placeholder="Type a tag…"
            data-testid="demand-tag-input"
            onChange={(e) => {
              setDraft(e.target.value)
              setActiveIdx(-1)
              setShowDropdown(true)
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            onBlur={() =>
              setTimeout(() => {
                setShowDropdown(false)
                if (!draftTrimmed) setAdding(false)
              }, 200)
            }
          />
          {showDropdown && totalOptions > 0 && (
            <div className={styles.dropdown} role="listbox">
              {showCreate && (
                <div
                  className={activeIdx === 0 ? styles.optionActive : styles.createOption}
                  role="option"
                  aria-selected={activeIdx === 0}
                  data-testid="demand-tag-create-option"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    createAndApply(draftTrimmed)
                  }}
                >
                  Create "{draftTrimmed}"
                </div>
              )}
              {suggestions.map((t, i) => {
                const optIdx = showCreate ? i + 1 : i
                return (
                  <div
                    key={t.id}
                    className={activeIdx === optIdx ? styles.optionActive : styles.option}
                    role="option"
                    aria-selected={activeIdx === optIdx}
                    data-testid={`demand-tag-option-${t.id}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      applyExisting(t.id)
                    }}
                  >
                    {t.label}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={styles.addButton}
          data-testid="demand-tag-add"
          onClick={() => setAdding(true)}
          disabled={isLoading}
        >
          + Add a tag
        </button>
      )}
    </div>
  )
}
